import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  input,
  runInInjectionContext,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { firstValueFrom, Observable } from 'rxjs';

import { filterDefined } from '@core/common-legacy';
import { PositionChangeApplyService } from '@core/tellent-positions-logic';
import { ConfirmPositionChangeDialogComponent } from '@core/tellent-positions-smart-ui';
import {
  ConfirmPositionChangeDialogData,
  ConfirmPositionChangeDialogResult,
  ConfirmPositionChangeOutcome,
} from '@core/tellent-positions-types';
import { TellentWebComponent } from '@core/tellent-types';
import { DialogWidth } from '@core/user-interface-legacy';

import { DialogWrapComponent } from '../dialog/dialog-wrap.component';

/**
 * Confirms a single member's move to another position and performs it.
 *
 * The host only knows what it is looking at — guids and names — so the whole dialog data is passed
 * in rather than resolved from the positions store, which the host app never loads. The assign or
 * reassign request runs here, in `mapResult`, and the host is handed the outcome: it has its own
 * save to continue with, and its own toasts to raise, so this wrapper deliberately raises none.
 */
@Component({
  selector: 'x-confirm-position-change-dialog',
  template: ``,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [],
})
export class TellentConfirmPositionChangeDialogWrapComponent extends DialogWrapComponent<
  ConfirmPositionChangeDialogComponent,
  ConfirmPositionChangeOutcome
> {
  private readonly injector = inject(Injector);
  private readonly positionChangeApplyService = inject(PositionChangeApplyService);

  public readonly component = ConfirmPositionChangeDialogComponent;
  public readonly config = {
    width: DialogWidth.Small,
    containerOptions: { fullscreenOnMobile: true },
  };

  public memberGuid = input<string>();
  public memberFullName = input<string>();
  public memberAvatarUrl = input<string | null>();
  public targetPositionGuid = input<string>();
  public targetPositionName = input<string>();
  public currentPositionGuid = input<string | null>();
  public currentPositionName = input<string | null>();

  public override async resolveDialogData(): Promise<ConfirmPositionChangeDialogData> {
    const memberGuid = await runInInjectionContext(this.injector, () =>
      firstValueFrom(toObservable(this.memberGuid).pipe(filterDefined)),
    );

    // The remaining inputs are meaningful when empty — a member with no position has no
    // `currentPositionName` — and the host sets every property in one pass without a defined order,
    // so they may still be unset when the guid resolves. Defer a microtask to let that pass land.
    return new Promise(resolve =>
      queueMicrotask(() =>
        resolve({
          targetPositionName: this.targetPositionName() ?? '',
          members: [
            {
              guid: memberGuid,
              fullName: this.memberFullName() ?? '',
              avatarThumbUrl: this.memberAvatarUrl() ?? null,
              currentPosition: this.resolveCurrentPosition(),
            },
          ],
        }),
      ),
    );
  }

  protected override mapResult(
    result: ConfirmPositionChangeDialogResult,
  ): Observable<ConfirmPositionChangeOutcome> {
    return this.positionChangeApplyService.apply(
      this.targetPositionGuid()!,
      [this.memberGuid()!],
      result,
    );
  }

  private resolveCurrentPosition() {
    const guid = this.currentPositionGuid();
    const name = this.currentPositionName();

    // The guid alone decides whether there is a position to move away from — a nameless one still
    // has to count, or the dialog claims the member holds none and hides the correction option.
    return guid ? { guid, name: name ?? '' } : null;
  }
}

export type TellentConfirmPositionChangeDialogWebComponent = TellentWebComponent<
  TellentConfirmPositionChangeDialogWrapComponent,
  | 'open'
  | 'memberGuid'
  | 'memberFullName'
  | 'memberAvatarUrl'
  | 'targetPositionGuid'
  | 'targetPositionName'
  | 'currentPositionGuid'
  | 'currentPositionName'
  | 'afterClosed'
>;
